import React from 'react';

import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { useQuery } from 'react-query';
import { useParams, Route, Routes, Navigate, NavLink } from 'react-router-dom';

import { fetchAccount } from './api';
import {
  Features,
  Users,
  Overview,
  Loader,
  BusinessPhones,
  BillingSetting,
  TwilioConfiguration,
} from './components';
import { Action } from './constants';
import { AccountProfileDispatch } from './context';
import { useAccountProfileReducer } from './reducer';

const AccountProfile: React.FC = () => {
  const { accountId } = useParams();
  const [state, dispatch] = useAccountProfileReducer();
  const { account } = state;
  const { isLoading, isError } = useQuery(['fetchAccount', accountId], fetchAccount, {
    onSuccess: (data) => {
      dispatch({
        type: Action.SET_ACCOUNT,
        account: data,
      });
    },
    refetchOnWindowFocus: false,
  });

  if (isError) return <Navigate to="../../accounts" />;

  if (isLoading || isEmpty(account)) return <Loader />;

  return (
    <AccountProfileDispatch.Provider value={{ state, dispatch }}>
      <div className="overflow-auto bg-white-0.0 sm:rounded-none rounded sm:px-6 lg:px-8 px-4 py-8 h-full">
        <div className="sm:px-6 px-4 py-5 bg-white-1.0 rounded">
          <h2 data-testid="name-header">{account.name}</h2>
        </div>
        <div className="sm:flex-row sm:space-y-0 sm:mt-6 sm:ml-6 flex flex-col mt-4 ml-0 space-y-4">
          <NavLink
            className={({ isActive }) =>
              classNames(
                'font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="overview"
            data-testid="account-profile-overview"
          >
            Overview
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              classNames(
                'sm:ml-16 ml-0 font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="features"
            data-testid="account-profile-features"
          >
            Features
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              classNames(
                'sm:ml-16 ml-0 font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="users"
            data-testid="account-profile-users"
          >
            Users
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              classNames(
                'sm:ml-16 ml-0 font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="business_phones"
            data-testid="account-profile-business-phones"
          >
            Business phones
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              classNames(
                'sm:ml-16 ml-0 font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="billing_settings"
            data-testid="account-profile-billing-settings"
          >
            Billing settings
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              classNames(
                'sm:ml-16 ml-0 font-semibold transition duration-100 ease-in-out cursor-pointer',
                isActive && 'border-b-2 border-secondary-1.0'
              )
            }
            to="twilio_configuration"
            data-testid="account-profile-twilio-configuration"
          >
            Twilio configuration
          </NavLink>
        </div>
        <Routes>
          <Route path="overview" element={<Overview />} />
          <Route path="features" element={<Features />} />
          <Route path="users" element={<Users />} />
          <Route path="business_phones" element={<BusinessPhones />} />
          <Route path="billing_settings" element={<BillingSetting />} />
          <Route path="twilio_configuration" element={<TwilioConfiguration />} />
          <Route path="*" element={<Navigate to="overview" />} />
        </Routes>
      </div>
    </AccountProfileDispatch.Provider>
  );
};

export default AccountProfile;
